import { memo } from 'react';

import { Badge, Flex, Heading, Logo, Text } from '@/components/core';
import { SetupRequired } from '@/components/flow';
import { useAgentIcon, useGetAgentFromPath } from '@/hooks';
import { toModelId, useGetModel } from '@/hooks/api';
import { listAllFields } from '@/utils';

export type ModelNodeProps = {
  modelId?: string;
};

const IModelNode = ({ modelId }: ModelNodeProps) => {
  const { data: model } = useGetModel(toModelId(modelId));
  const allFields = listAllFields(model?.fieldConfig ?? {});
  const visibleFieldNames = allFields.slice(0, 6);
  const restFieldNames = allFields.length - visibleFieldNames.length;
  const { data: agent } = useGetAgentFromPath();
  const agentIcon = useAgentIcon({ agentId: agent?.agentId });

  return (
    <Flex col className="w-full">
      <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
        <Logo size="lg">{agentIcon}</Logo>
        <Heading size="lg">Extract with AI</Heading>
      </div>
      <div className="gap-2 pt-2">
        {Object.entries(model?.fieldConfig ?? {}).length ? (
          <Text className="flex flex-row flex-wrap items-center gap-2 text-gray-600">
            Extract:{' '}
            {visibleFieldNames.map(({ name }) => (
              <Badge key={name} variant="plain">
                <Text>{name}</Text>
              </Badge>
            ))}
            {restFieldNames ? (
              <Badge key="rest" variant="plain">
                <Text>+{restFieldNames}</Text>
              </Badge>
            ) : null}
          </Text>
        ) : (
          <SetupRequired />
        )}
      </div>
    </Flex>
  );
};

export const ModelNode = memo(IModelNode) as typeof IModelNode;
